import Grid from "@mui/material/Grid";
import { useState, useEffect, useMemo, useCallback } from "react";

import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import SplitCardLayout from "components/layout/SplitCardLayout";
import { useAnalytics } from "context/AnalyticsContext";
import axiosInstance from "services/httpInterceptor";
import PublishingStrategyCards from "./components/ContentPublishingStrategy/PublishingStrategyCards";
import ChartInfoTooltip from "components/Tooltip/ChartInfoTooltip";
import { chartTooltips } from "components/Tooltip/Definitions/data";
import EffectivePublishingTimes from "./components/ContentPublishingStrategy/EffectivePublishingTime";
import EffectiveHashtags from "./components/ContentPublishingStrategy/EffectiveHashtags";
import PublishingPatternContainer from "./components/ContentPublishingStrategy/PublishingHeatMap/data";
import ContentCategoryDistribution from "./components/ContentAnalysis";
import MarketCategoryEngagement from "./components/MarketCategoryEngagement";
import CompetitorEfficiencyChart from "./components/CompetitorEfficiencyChart";
import PostingVolumeChart from "./components/PostingVolumeChart";
import { analyticsService } from "services/trackingService";

const CONTENT_FOCUS_CATEGORIES = [
  "Clinical Evidence",
  "Product & Technology",
  "Education & Case Sharing",
  "Events & Congresses",
  "Company & Strategy",
  "Other",
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const meanOf = (values) => {
  const valid = values.filter((v) => v != null && !isNaN(v) && v > 0);
  if (valid.length === 0) return null;
  return valid.reduce((s, v) => s + v, 0) / valid.length;
};

const fetchStats = (entry) =>
  axiosInstance
    .get("/v1/frontend/linkedin/v2/general/stats", {
      params: { companyId: entry.id, preset: "LAST_QUARTER" },
    })
    .then((r) => ({
      ...entry,
      avgEngagementsPerPost: r.data?.avgEngagementsPerPost ?? 0,
      totalPosts: r.data?.totalPosts ?? 0,
    }))
    .catch(() => ({ ...entry, avgEngagementsPerPost: 0, totalPosts: 0 }));

const fetchPosts = (companyId) =>
  axiosInstance
    .get("/v1/linkedin/posts/paged", {
      params: { companyId, preset: "LAST_QUARTER", page: 0, size: 100 },
    })
    .then((r) => r.data?.content || [])
    .catch(() => []);

// ── Market category computation ───────────────────────────────────────────────
//
// Uses a single market-wide baseline so bars are directly comparable across
// companies. Algorithm:
//   1. marketBaseline = mean(likes + comments + shares) across ALL posts
//   2. postVsMarket = post raw engagement / marketBaseline
//   3. categoryVsMarket = mean(postVsMarket) for all posts in that category
//
// This is intentionally different from post.vsAvg on the feed cards, which
// normalises each post against its own company's average. That per-company
// metric is the right signal when browsing a competitor's feed ("did this post
// punch above Medtronic's usual weight?"). The market-normalised metric here
// answers a different question: "which content category performs best across
// the whole market, regardless of who posted it?"

const computeCategoryData = (allPosts) => {
  if (allPosts.length === 0) return [];

  const rawEngagement = (p) =>
    (p.likes ?? 0) + (p.comments ?? 0) + (p.shares ?? 0);

  const baseline = meanOf(allPosts.map(rawEngagement));
  if (!baseline || baseline === 0) return [];

  const buckets = {};
  allPosts.forEach((post) => {
    const cat = post.analysis?.type;
    if (!cat) return;
    const vsMarket = rawEngagement(post) / baseline;
    if (!buckets[cat]) buckets[cat] = [];
    buckets[cat].push(vsMarket);
  });

  return CONTENT_FOCUS_CATEGORIES
    .map((cat) => {
      const vals = buckets[cat] || [];
      const mean = meanOf(vals);
      return { category: cat, mean, count: vals.length };
    })
    .filter((d) => d.mean != null && d.count > 0)
    .sort((a, b) => b.mean - a.mean);
};

// ── Page component ────────────────────────────────────────────────────────────

function ContentStrategy() {
  const { companyId, companyName, competitors } = useAnalytics();

  // ── Stats fetch (for Efficiency + Volume charts + Market Avg Engagement minicard) ──
  const [statsData, setStatsData] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);

  const allEntries = useMemo(() => {
    if (!companyId) return [];
    return [
      { id: companyId, name: companyName || "Your Company", isClient: true },
      ...(competitors || []).map((c) => ({ id: c.id, name: c.name, isClient: false })),
    ];
  }, [companyId, companyName, competitors]);

  const runStatsFetch = useCallback(async () => {
    if (allEntries.length === 0) return;
    setStatsLoading(true);
    setStatsError(false);
    try {
      setStatsData(await Promise.all(allEntries.map(fetchStats)));
    } catch {
      setStatsError(true);
    } finally {
      setStatsLoading(false);
    }
  }, [allEntries]);

  useEffect(() => { runStatsFetch(); }, [runStatsFetch]);

  // ── Posts fetch (for Market Engagement by Category chart + Effective Content minicard) ──
  const competitorIds = useMemo(
    () => (competitors || []).map((c) => c.id),
    [competitors]
  );

  const [allPosts, setAllPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [postsError, setPostsError] = useState(false);

  const runPostsFetch = useCallback(async () => {
    if (!companyId || competitorIds.length === 0) {
      setPostsLoading(false);
      return;
    }
    setPostsLoading(true);
    setPostsError(false);
    try {
      const results = await Promise.all(competitorIds.map(fetchPosts));
      setAllPosts(results.flat());
    } catch {
      setPostsError(true);
    } finally {
      setPostsLoading(false);
    }
  }, [companyId, competitorIds]);

  useEffect(() => { runPostsFetch(); }, [runPostsFetch]);

  // ── Derived values ────────────────────────────────────────────────────────

  // Market avg engagement — mean of competitor avgEngagementsPerPost (excludes client)
  const marketAvgEngagement = useMemo(() =>
    meanOf(statsData.filter((s) => !s.isClient).map((s) => s.avgEngagementsPerPost)),
    [statsData]
  );

  // Market-normalised category ranking — shared by chart and minicard
  const categoryData = useMemo(() => computeCategoryData(allPosts), [allPosts]);

  // Top category by market-normalised engagement — feeds "Effective Content" minicard
  const topCategory = categoryData.length > 0 ? categoryData[0].category : null;

  useEffect(() => {
    analyticsService.trackPageView("MARKET_OVERVIEW", { companyId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>

        {/* ── Section 1: Content Publishing Strategy ── */}
        <VuiBox mb={3} sx={{ minHeight: "50px" }}>
          <VuiTypography variant="h5" color="white" fontWeight="bold" sx={{ fontSize: "20px" }}>
            Content Publishing Strategy
          </VuiTypography>
          <VuiTypography variant="button" color="text" fontWeight="regular" sx={{ fontSize: "12px" }}>
            When and how competitors are publishing content based on last quarter's data.
          </VuiTypography>
        </VuiBox>

        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <PublishingStrategyCards
              companyId={companyId}
              marketAvgEngagement={marketAvgEngagement}
              topCategory={topCategory}
            />
          </Grid>
        </VuiBox>

        <VuiBox mb={3}>
          <SplitCardLayout
            leftCard={
              <ChartInfoTooltip
                title="Content Publishing Patterns"
                tooltipText={chartTooltips.publishingPatternsHeatmap}
              >
                <PublishingPatternContainer companyId={companyId} />
              </ChartInfoTooltip>
            }
            rightCards={[
              <EffectivePublishingTimes companyId={companyId} limit={2} />,
              <EffectiveHashtags companyId={companyId} limit={2} />,
            ]}
          />
        </VuiBox>

        {/* ── Section 2: Content Analysis ── */}
        <VuiBox mb={3} sx={{ minHeight: "50px" }}>
          <VuiTypography variant="h5" color="white" fontWeight="bold" sx={{ fontSize: "20px" }}>
            Content Analysis
          </VuiTypography>
          <VuiTypography variant="button" color="text" fontWeight="regular" sx={{ fontSize: "12px" }}>
            What content drives engagement and how competitors are positioned — last quarter.
          </VuiTypography>
        </VuiBox>

        <VuiBox mb={3}>
          <Grid container spacing={3}>

            {/* Row 1: Engagement Efficiency + Posting Volume — side by side */}
            <Grid item xs={12} md={6}>
              <CompetitorEfficiencyChart
                stats={statsData}
                loading={statsLoading}
                error={statsError}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <PostingVolumeChart
                stats={statsData}
                loading={statsLoading}
                error={statsError}
              />
            </Grid>

            {/* Row 2: Market Engagement by Category — full width */}
            <Grid item xs={12}>
              <MarketCategoryEngagement
                categoryData={categoryData}
                loading={postsLoading}
                error={postsError}
              />
            </Grid>

            {/* Row 3: Competitors Published Content Categories — full width */}
            <Grid item xs={12}>
              <ContentCategoryDistribution companyId={companyId} chartType="postType" />
            </Grid>

          </Grid>
        </VuiBox>

      </VuiBox>
    </DashboardLayout>
  );
}

export default ContentStrategy;
